import { ApiError, isApiProblem, NetworkError, type ApiProblem } from './problem';
import { refreshCoordinator, type RefreshCoordinator } from './refresh-coordinator';
import { authState } from '../stores/auth';
import { currentLocale } from '../i18n/locale';

export const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

/** The double-submit CSRF cookie of `14 §5.3`, readable by design. */
const CSRF_COOKIE = '__Host-absor_csrf';

/**
 * The one in-flight refresh.
 *
 * **This is the most important line in the file.** Refresh tokens rotate, and
 * presenting a rotated token a second time is treated as theft: the backend
 * burns the whole chain and signs the user out everywhere (block 1C). Two
 * requests whose tokens expire in the same tick would each start a refresh, the
 * second would present a token the first had already rotated, and the session
 * would die — under load, and only under load. One shared promise means the
 * second request waits for the first instead of racing it.
 *
 * Esto deduplica dentro de **una** pestaña; la misma carrera entre pestañas la
 * resuelve el coordinador de `refresh-coordinator.ts`, porque una variable de
 * módulo no cruza contextos y cada pestaña arranca con su propio refresh.
 */
let inFlightRefresh: Promise<string | null> | null = null;

/** Sustituible solo en tests, vía `setRefreshCoordinatorForTests`. */
let coordinator: RefreshCoordinator = refreshCoordinator;

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

async function readProblem(response: Response): Promise<ApiProblem> {
  try {
    const body: unknown = await response.clone().json();

    if (isApiProblem(body)) {
      return body;
    }
  } catch {
    // Not JSON — fall through to the synthesised problem below.
  }

  return {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    request_id: response.headers.get('X-Request-Id') ?? 'unknown',
  };
}

/**
 * Exchanges the refresh cookie for a new access token, at most once at a time.
 *
 * Returns null when the session is genuinely over, which is the caller's cue to
 * stop retrying and send the user to the login screen.
 */
export function refreshSession(): Promise<string | null> {
  inFlightRefresh ??= (async () => {
    // Si otra pestaña acuña un token mientras esta espera el candado, se adopta
    // el suyo y la cookie no se gasta: dos POST con el mismo refresh token son
    // exactamente la señal de robo que el backend castiga.
    let announcedWhileWaiting: string | null = null;
    const unsubscribe = coordinator.onToken((token) => {
      announcedWhileWaiting = token;
    });

    try {
      return await coordinator.withLock(async () => {
        if (announcedWhileWaiting !== null) {
          authState.set(announcedWhileWaiting);

          return announcedWhileWaiting;
        }

        const csrf = readCookie(CSRF_COOKIE);

        // Two attempts, because of one specific answer: the edge rate-limits
        // `/api/v1/auth` per address (control S-11), and a burst of hard reloads
        // can meet a 429 here. That answer means "slow down", not "signed out" —
        // clearing the session on it logged people out for pressing F5, which the
        // orchestration E2E was the first to do fast enough to prove.
        for (let attempt = 0; ; attempt += 1) {
          const response = await fetch(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              // Only meaningful when the token came from the cookie, which is the
              // case here; the backend checks it exactly then.
              ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
            },
            body: '{}',
          });

          if (response.status === 429 && attempt === 0) {
            const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
            await new Promise((resolve) => setTimeout(resolve, Number.isNaN(retryAfter) ? 4_000 : retryAfter * 1_000));
            continue;
          }

          if (!response.ok) {
            // Only an answer that says the session itself is over clears it. A
            // second 429 or a 5xx behaves like the network failure below: the
            // next attempt may still succeed, and signing out would destroy a
            // session the server never revoked.
            if (response.status === 401 || response.status === 403) {
              authState.clear();
            }

            return null;
          }

          const body = (await response.json()) as { access_token?: string };
          const token = body.access_token ?? null;
          authState.set(token);

          if (token !== null) {
            coordinator.announceToken(token);
          }

          return token;
        }
      });
    } catch {
      // A network failure is not an expired session: leave the token alone so
      // the next attempt can still use it.
      return null;
    } finally {
      unsubscribe();
      // Cleared in `finally` so a failed refresh does not wedge every later
      // request behind a permanently rejected promise.
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Set by the retry to stop a second 401 from looping. */
  retried?: boolean;
  /** Hand back the `Response` instead of parsing it; see {@link authText}. */
  rawResponse?: boolean;
}

/**
 * Every call to the API goes through here.
 *
 * A wrapper around `fetch` rather than an `openapi-fetch` middleware: a
 * middleware sees the response but not a request it can cleanly re-issue, and
 * replaying a consumed body is exactly what the 401 retry needs to do.
 */
export async function authFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retried, headers, rawResponse, ...rest } = options;
  const token = authState.token();

  let response: Response;

  try {
    const init: RequestInit = {
      ...rest,
      credentials: 'include',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // El idioma **activo del SPA**, no el del navegador. Sin esto los dos
        // lados se contradicen en la primera visita: la pantalla en el idioma
        // que eligió la persona y el `detail` de un 422 en el que trae
        // configurado el Chrome. Con sesión el backend prefiere `users.locale`
        // y esta cabecera no estorba; sin ella es la única fuente que tiene.
        'Accept-Language': currentLocale(),
        ...headers,
      },
    };

    // Assigned rather than spread: with `exactOptionalPropertyTypes`, an
    // explicit `body: undefined` is not the same as an absent `body`, and
    // `fetch` refuses the former on a GET.
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    response = await fetch(`${BASE_URL}${path}`, init);
  } catch (cause) {
    throw new NetworkError(cause);
  }

  // One retry, and never for the refresh call itself — that would recurse.
  if (response.status === 401 && !retried && !path.startsWith('/auth/refresh')) {
    const refreshed = await refreshSession();

    if (refreshed) {
      return authFetch<T>(path, { ...options, retried: true });
    }
  }

  if (!response.ok) {
    throw new ApiError(await readProblem(response));
  }

  if (rawResponse === true) return response as T;

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * The same call, for the endpoints that answer bytes instead of JSON.
 *
 * There is exactly one today — `GET /artifacts/{id}/content`, which streams an
 * artifact as a download — and it exists because an `<a href>` cannot carry the
 * bearer token. Written as a thin wrapper over {@link authFetch} rather than as
 * a second implementation so that the refresh-on-401 dance, the locale header
 * and the problem-document parsing stay in one place.
 */
export async function authText(path: string, options: RequestOptions = {}): Promise<string> {
  // The cast is the whole trick: `authFetch` is generic over what it parses,
  // and here it parses nothing. The `Response` never reaches the caller.
  return authFetch<Response>(path, { ...options, rawResponse: true }).then((response) => response.text());
}

/**
 * Only for tests: the shared promise is module state, and a test that leaves one
 * behind changes the next test's behaviour.
 */
export function resetRefreshState(): void {
  inFlightRefresh = null;
  coordinator = refreshCoordinator;
}

/**
 * Only for tests: en jsdom no hay `navigator.locks` ni `BroadcastChannel`, así
 * que los tests que ejercitan la coordinación inyectan aquí un doble. Lo
 * deshace `resetRefreshState()`.
 */
export function setRefreshCoordinatorForTests(next: RefreshCoordinator): void {
  coordinator = next;
}
